import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Scale, ExternalLink } from "lucide-react";
import { BarChart, Bar, Cell, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, ReferenceLine } from "recharts";
import { fmt, tooltipStyle } from "./oversiktConstants";
import { dec, num } from "@/lib/formatters";

const SOURCE_LINKS = { "SSB 14310": "14310", "SSB 09895": "09895", "SSB 12842": "12842", "SSB 11573": "11573" };

const diffBadge = (d) => {
  if (d == null) return <span className="text-[#8AA4BF]">—</span>;
  const cls = d > 2 ? "bg-[#FBEAE7] text-[#B23B2E]" : d < -2 ? "bg-[#E6F3F2] text-[#2E6B68]" : "bg-[#F1F4F8] text-[#4B647F]";
  const word = d > 2 ? "dyrare" : d < -2 ? "billigere" : "som landet";
  return <span className={`inline-block px-2 py-0.5 rounded-full text-xs font-medium ${cls}`}>{d > 0 ? "+" : ""}{dec(d, 1)} % {word}</span>;
};

const LevekostnadCard = ({ data }) => {
  const items = data?.items || [];
  if (!items.length) return null;
  const SHORT = { bokostnad: "Bokostnad", boligpris: "Bustadpris", leie: "Leige", gebyrer: "Gebyr", barnehage: "Barnehage" };
  const chartData = items.map((i) => ({ name: SHORT[i.key] || i.label, diff: i.diff_pct ?? 0, key: i.key }));
  const idx = data.index;

  return (
    <Card className="bg-white border border-[#E6E9F0] rounded-lg mb-8" data-testid="levekostnad-section" id="levekostnad">
      <CardHeader className="border-b border-[#E6E9F0] pb-4">
        <CardTitle className="text-lg font-medium text-[#1F2A36] flex items-center gap-2">
          <Scale className="w-5 h-5 text-[#103766]" />
          Er det dyrt å bu her? Levekostnader mot landet
        </CardTitle>
        <p className="text-sm text-[#4B647F]">Sentrale kostnader for innbyggjarane i kommunen samanlikna med gjennomsnittet for Noreg</p>
      </CardHeader>
      <CardContent className="pt-6">
        <div className="grid grid-cols-1 xl:grid-cols-5 gap-6">
          <div className="xl:col-span-2 space-y-4">
            {idx != null && (
              <div className={`p-5 rounded-lg border ${idx > 102 ? "border-[#D35E4E] bg-[#FBEAE7]/40" : idx < 98 ? "border-[#3F8A86] bg-[#E6F3F2]/40" : "border-[#E6E9F0] bg-[#F8FAFC]"}`} data-testid="levekostnad-index">
                <p className="text-xs font-semibold uppercase tracking-[0.1em] text-[#8AA4BF]">Levekostnadsindeks (landet = 100)</p>
                <p className="text-5xl font-light text-[#1F2A36] mt-1">{dec(idx, 1)}</p>
                <p className="text-sm text-[#4B647F] mt-1">
                  {idx > 102 ? "Dyrare enn landsgjennomsnittet" : idx < 98 ? "Billegare enn landsgjennomsnittet" : "Om lag som landsgjennomsnittet"}
                </p>
                <p className="text-[11px] text-[#8AA4BF] mt-2">{data.index_note}</p>
              </div>
            )}
            <div className="h-[220px]" data-testid="levekostnad-chart">
              <ResponsiveContainer width="100%" height="100%">
                <BarChart data={chartData} layout="vertical" margin={{ left: 8, right: 16 }}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#E6E9F0" />
                  <XAxis type="number" stroke="#8AA4BF" fontSize={11} tickFormatter={(v) => `${v > 0 ? "+" : ""}${num(v)} %`} />
                  <YAxis type="category" dataKey="name" stroke="#8AA4BF" fontSize={11} width={80} interval={0} />
                  <ReferenceLine x={0} stroke="#4B647F" />
                  <Tooltip formatter={(v) => `${v > 0 ? "+" : ""}${num(v)} % mot landet`} contentStyle={tooltipStyle} />
                  <Bar dataKey="diff" radius={[0, 4, 4, 0]}>
                    {chartData.map((d) => <Cell key={d.key} fill={d.diff > 0 ? "#D35E4E" : "#3F8A86"} />)}
                  </Bar>
                </BarChart>
              </ResponsiveContainer>
            </div>
          </div>
          <div className="xl:col-span-3 overflow-x-auto">
            <table className="w-full text-sm" data-testid="levekostnad-table">
              <thead>
                <tr className="text-xs text-[#8AA4BF]">
                  <th className="text-left font-medium pb-2">Kostnad</th>
                  <th className="text-right font-medium pb-2">Kommunen</th>
                  <th className="text-right font-medium pb-2">Landet</th>
                  <th className="text-right font-medium pb-2">Avvik</th>
                </tr>
              </thead>
              <tbody>
                {items.map((i) => (
                  <tr key={i.key} className="border-t border-[#E6E9F0]" data-testid={`levekostnad-row-${i.key}`}>
                    <td className="py-2 text-[#1F2A36]">
                      {i.label}
                      {i.estimate && <Badge variant="outline" className="ml-2 border-[#E8A34A] text-[#B8741F] bg-[#FDF6EA] text-[10px] uppercase">Estimat</Badge>}
                      <p className="text-[11px] text-[#8AA4BF]">
                        {i.year ? `${String(i.year).replace("K", " K")} · ` : ""}
                        {SOURCE_LINKS[i.source] ? (
                          <a href={`https://www.ssb.no/statbank/table/${SOURCE_LINKS[i.source]}`} target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-1 text-[#103766] hover:underline">{i.source} <ExternalLink className="w-3 h-3" /></a>
                        ) : i.source}
                      </p>
                    </td>
                    <td className="text-right tabular-nums font-medium text-[#1F2A36] align-top py-2">{fmt(i.kommune)} {i.unit}</td>
                    <td className="text-right tabular-nums text-[#4B647F] align-top py-2">{i.land != null ? `${fmt(i.land)} ${i.unit}` : "—"}</td>
                    <td className="text-right align-top py-2">{diffBadge(i.diff_pct)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
            <p className="text-[11px] text-[#8AA4BF] mt-3">
              Landet = SSB-aggregat der det finst, elles gjennomsnitt av alle kommunar (bokostnad). Leige gjeld kommunens prissone i leigemarknadsundersøkinga.
            </p>
          </div>
        </div>
      </CardContent>
    </Card>
  );
};

export default LevekostnadCard;
